import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, toast } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { ArrowRight, Copy, KeyRound, Plug, RefreshCw, Store } from 'lucide-react';
import { UI_NAV_LABELS } from '@/constants/uiLabels';

const defaultConnection = (platform) => ({
  id: null,
  platform,
  status: 'not_connected',
  store_identifier: null,
  has_access_token: false,
  has_api_keys: false,
  has_api_key: false,
  api_key_masked: null,
  import_endpoint: null,
  ping_endpoint: null,
});

const IntegrationsPanel = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publicApiBusy, setPublicApiBusy] = useState(false);
  const [revealedApiKey, setRevealedApiKey] = useState('');
  const [items, setItems] = useState([]);
  const [isProActive, setIsProActive] = useState(false);
  const [shopifyStatus, setShopifyStatus] = useState(null);
  const [shopifyStore, setShopifyStore] = useState('');
  const [shopifyToken, setShopifyToken] = useState('');
  const [wooStore, setWooStore] = useState('');
  const [wooKey, setWooKey] = useState('');
  const [wooSecret, setWooSecret] = useState('');
  const publicImportEndpointFallback = `${API}/public/orders/import`;
  const publicPingEndpointFallback = `${API}/public/ping`;
  const publicApiPayloadExample = JSON.stringify(
    {
      external_order_id: 'CMD-12345',
      customer_name: 'Jean Dupont',
      customer_phone: '+242060000000',
      pickup_address: 'Brazzaville centre',
      pickup_reference: 'Portail noir',
      delivery_address: 'Poto-Poto',
      delivery_reference: 'En face du marche',
      city: 'BZV',
      package_value: 15000,
      service_type: 'standard',
      insurance: true,
    },
    null,
    2,
  );

  const loadIntegrations = async () => {
    setLoading(true);
    try {
      const [integrationsResponse, shopifyStatusResponse] = await Promise.all([
        axios.get(`${API}/integrations`),
        axios.get(`${API}/integrations/shopify/status`).catch(() => ({ data: null })),
      ]);
      setItems(integrationsResponse.data?.items || []);
      setIsProActive(Boolean(integrationsResponse.data?.is_pro_active || integrationsResponse.data?.entitlements?.is_pro_active));
      setShopifyStatus(shopifyStatusResponse.data?.data || null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de charger les integrations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadIntegrations();
  }, []);

  const connectionByPlatform = useMemo(() => {
    const map = {
      shopify: defaultConnection('shopify'),
      woocommerce: defaultConnection('woocommerce'),
      public_api: defaultConnection('public_api'),
    };
    items.forEach((item) => {
      map[item.platform] = item;
    });
    return map;
  }, [items]);

  const copyToClipboard = async (value, successMessage) => {
    if (!value) {
      toast.error('Aucune valeur a copier');
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      toast.success(successMessage);
    } catch {
      toast.error('Copie impossible');
    }
  };

  const connectShopify = async () => {
    if (!shopifyStore || !shopifyToken) {
      toast.error('Identifiant boutique et jeton Shopify requis');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/integrations/shopify/connect`, {
        store_identifier: shopifyStore,
        access_token: shopifyToken,
      });
      toast.success('Shopify connecte');
      setShopifyStore('');
      setShopifyToken('');
      await loadIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Echec de connexion Shopify');
    } finally {
      setSaving(false);
    }
  };

  const disconnectShopify = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/integrations/shopify/disconnect`);
      setShopifyStore('');
      setShopifyToken('');
      toast.success('Shopify deconnecte');
      await loadIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Echec de deconnexion Shopify');
    } finally {
      setSaving(false);
    }
  };

  const connectWooCommerce = async () => {
    if (!wooStore || !wooKey || !wooSecret) {
      toast.error('Identifiant boutique, cle API et secret API WooCommerce requis');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/integrations/woocommerce/connect`, {
        store_identifier: wooStore,
        api_key: wooKey,
        api_secret: wooSecret,
      });
      toast.success('WooCommerce connecte');
      setWooSecret('');
      await loadIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Echec de connexion WooCommerce');
    } finally {
      setSaving(false);
    }
  };

  const renderStatusBadge = (status) => {
    if (status === 'connected') {
      return <Badge className="bg-green-100 text-green-700">Connecte</Badge>;
    }
    if (status === 'pending') {
      return <Badge className="bg-amber-100 text-amber-700">En attente</Badge>;
    }
    return <Badge className="bg-gray-100 text-gray-700">Non connecte</Badge>;
  };

  const generateOrRegeneratePublicApiKey = async (mode = 'generate') => {
    setPublicApiBusy(true);
    try {
      const endpoint = mode === 'regenerate'
        ? `${API}/integrations/public-api/regenerate-key`
        : `${API}/integrations/public-api/generate-key`;
      const response = await axios.post(endpoint);
      const apiKey = response.data?.data?.api_key || '';
      setRevealedApiKey(apiKey);
      await loadIntegrations();
      toast.success(mode === 'regenerate' ? 'Cle API regeneree' : 'Cle API generee');
      if (apiKey) {
        await copyToClipboard(apiKey, 'Cle API copiee');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de generer la cle API');
    } finally {
      setPublicApiBusy(false);
    }
  };

  const copyPublicApiKey = async () => {
    setPublicApiBusy(true);
    try {
      let apiKey = revealedApiKey;
      if (!apiKey) {
        const response = await axios.get(`${API}/integrations/public-api/key`);
        apiKey = response.data?.data?.api_key || '';
        if (!apiKey) {
          throw new Error('missing_api_key');
        }
        setRevealedApiKey(apiKey);
      }
      await copyToClipboard(apiKey, 'Cle API copiee');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de recuperer la cle API');
    } finally {
      setPublicApiBusy(false);
    }
  };

  const publicApiConnection = connectionByPlatform.public_api;
  const publicImportEndpoint = publicApiConnection.import_endpoint || publicImportEndpointFallback;
  const publicPingEndpoint = publicApiConnection.ping_endpoint || publicPingEndpointFallback;
  const hasPublicApiKey = Boolean(publicApiConnection.has_api_key || publicApiConnection.has_api_keys);
  const shopifyConnected = Boolean(shopifyStatus?.connected || connectionByPlatform.shopify.status === 'connected');
  const shopifyStoreIdentifier = shopifyStatus?.store_identifier || connectionByPlatform.shopify.store_identifier || null;
  const shopifyProStatus = Boolean(shopifyStatus?.pro_active ?? isProActive);
  const shopifyImportEnabled = Boolean(shopifyStatus?.import_enabled);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-2xl flex items-center gap-2">
          <Plug className="w-6 h-6 text-red-600" />
          {UI_NAV_LABELS.integrations}
        </CardTitle>
        <CardDescription>
          Shopify, WooCommerce et API publique. Le backend reste la source de verite.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!isProActive && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
            Necessite un abonnement Coli Noki Pro actif.
          </div>
        )}

        {loading ? (
          <div className="py-8 text-center text-gray-500">Chargement des integrations...</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center justify-between text-lg">
                    <span className="flex items-center gap-2">
                      <Store className="w-4 h-4" />
                      Shopify
                    </span>
                    {renderStatusBadge(shopifyConnected ? 'connected' : connectionByPlatform.shopify.status)}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {shopifyConnected ? (
                    <div className="space-y-3 rounded-lg border bg-slate-50 p-3">
                      <div>
                        <div className="text-sm font-medium text-slate-900">Boutique connectee</div>
                        <div className="break-all font-mono text-xs text-slate-600">
                          {shopifyStoreIdentifier || 'Shopify lie'}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 text-xs">
                        <Badge className={shopifyProStatus ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                          Pro {shopifyProStatus ? 'actif' : 'inactif'}
                        </Badge>
                        <Badge className={shopifyImportEnabled ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-700'}>
                          Import {shopifyImportEnabled ? 'actif' : 'suspendu'}
                        </Badge>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        onClick={disconnectShopify}
                        disabled={saving}
                      >
                        Deconnecter Shopify
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Input
                        placeholder="Identifiant boutique"
                        value={shopifyStore}
                        onChange={(event) => setShopifyStore(event.target.value)}
                      />
                      <Input
                        placeholder="Jeton d'acces"
                        value={shopifyToken}
                        onChange={(event) => setShopifyToken(event.target.value)}
                      />
                      <Button
                        className="w-full"
                        onClick={connectShopify}
                        disabled={saving || !isProActive}
                      >
                        Connecter Shopify
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center justify-between text-lg">
                    <span className="flex items-center gap-2">
                      <Store className="w-4 h-4" />
                      WooCommerce
                    </span>
                    {renderStatusBadge(connectionByPlatform.woocommerce.status)}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Input
                    placeholder="Identifiant boutique"
                    value={wooStore}
                    onChange={(event) => setWooStore(event.target.value)}
                  />
                  <Input
                    placeholder="Cle API"
                    value={wooKey}
                    onChange={(event) => setWooKey(event.target.value)}
                  />
                  <Input
                    placeholder="Secret API"
                    value={wooSecret}
                    onChange={(event) => setWooSecret(event.target.value)}
                  />
                  <Button
                    className="w-full"
                    onClick={connectWooCommerce}
                    disabled={saving || !isProActive}
                  >
                    Connecter WooCommerce
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center justify-between text-lg">
                    <span className="flex items-center gap-2">
                      <KeyRound className="w-4 h-4" />
                      API publique
                    </span>
                    {renderStatusBadge(isProActive ? publicApiConnection.status : 'not_connected')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Connectez votre boutique ou votre systeme via l'API Coli Noki.
                  </p>

                  <div className="space-y-2 rounded-lg border bg-slate-50 p-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Cle API</div>
                    <div className="break-all font-mono text-sm text-slate-900">
                      {publicApiConnection.api_key_masked || 'Aucune cle API generee'}
                    </div>
                    {revealedApiKey && (
                      <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-800">
                        Nouvelle cle active :
                        <div className="mt-1 break-all font-mono text-[11px]">{revealedApiKey}</div>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={copyPublicApiKey}
                        disabled={publicApiBusy || !isProActive || !hasPublicApiKey}
                      >
                        <Copy className="mr-2 h-4 w-4" />
                        Copier
                      </Button>
                      <Button
                        type="button"
                        onClick={() => generateOrRegeneratePublicApiKey(hasPublicApiKey ? 'regenerate' : 'generate')}
                        disabled={publicApiBusy || !isProActive}
                      >
                        <RefreshCw className="mr-2 h-4 w-4" />
                        {hasPublicApiKey ? 'Regenerer' : 'Generer'}
                      </Button>
                    </div>
                    <p className="text-[11px] text-slate-500">
                      Utilisez cette cle dans l'en-tete Authorization: Bearer {'{API_KEY}'}.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Endpoint d'import</div>
                    <div className="break-all rounded-md border bg-white p-2 font-mono text-xs">
                      POST {publicImportEndpoint}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => copyToClipboard(publicImportEndpoint, 'Endpoint copie')}
                        disabled={publicApiBusy}
                      >
                        <Copy className="mr-2 h-4 w-4" />
                        Copier l'endpoint
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => copyToClipboard(publicPingEndpoint, 'Endpoint ping copie')}
                        disabled={publicApiBusy}
                      >
                        <Copy className="mr-2 h-4 w-4" />
                        Copier ping
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Exemple JSON</div>
                    <Textarea
                      readOnly
                      value={publicApiPayloadExample}
                      className="min-h-[180px] font-mono text-xs"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="border-cyan-100 bg-cyan-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Configuration d'un cote, operations de l'autre</CardTitle>
                <CardDescription>
                  Cette page reste dediee a la connexion et a la configuration. Les commandes importees se traitent dans une rubrique vendeur separee.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-cyan-950">
                  Accedez ensuite a l'espace operationnel pour consulter les imports, verifier les snapshots prix / SLA et convertir les commandes en livraisons Coli Noki.
                </p>
                <Button asChild className="bg-red-500 hover:bg-red-600" data-testid="integrations-imported-orders-link">
                  <Link to="/vendeur/imported-orders">
                    Ouvrir les commandes importees
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default IntegrationsPanel;
